import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import {
  Send,
  Mail,
  Loader2,
  CheckCircle2,
  Trash2,
  RefreshCw,
  Plus,
  X,
  Languages,
  Inbox,
  Star,
  Archive,
  FileText,
  AlertTriangle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Reply,
  Forward,
  Sparkles,
  Paperclip,
  Clock,
  Search,
  Edit3,
  Edit2,
  Check,
  Globe,
  Folder,
  FolderPlus,
  FolderInput,
  Tag,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Save,
  Settings,
  MailOpen,
  Upload,
  Download,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import RichTextEditor from './RichTextEditor';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Gmail-style folder configuration
const FOLDERS = {
  inbox: { icon: Inbox, label: { es: 'Recibidos', en: 'Inbox' }, color: 'text-slate-700' },
  starred: { icon: Star, label: { es: 'Destacados', en: 'Starred' }, color: 'text-amber-500' },
  sent: { icon: Send, label: { es: 'Enviados', en: 'Sent' }, color: 'text-slate-700' },
  drafts: { icon: FileText, label: { es: 'Borradores', en: 'Drafts' }, color: 'text-slate-700' },
  important: { icon: Star, label: { es: 'Importantes', en: 'Important' }, color: 'text-amber-500', filled: true },
  archived: { icon: Archive, label: { es: 'Archivados', en: 'Archived' }, color: 'text-slate-700' },
  spam: { icon: AlertTriangle, label: { es: 'Spam', en: 'Spam' }, color: 'text-orange-500' },
  trash: { icon: Trash2, label: { es: 'Papelera', en: 'Trash' }, color: 'text-slate-500' },
};

// Translation languages
const TRANSLATION_LANGUAGES = [
  { code: 'es', name: 'Español' },
  { code: 'en', name: 'English' },
  { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Français' },
  { code: 'it', name: 'Italiano' },
  { code: 'pt', name: 'Português' },
  { code: 'pl', name: 'Polski' },
  { code: 'nl', name: 'Nederlands' },
];

const EmailToolTab = () => {
  const { language } = useLanguage();
  
  // Main state
  const [currentFolder, setCurrentFolder] = useState('inbox');
  const [emailPage, setEmailPage] = useState(1);
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Compose state
  const [showCompose, setShowCompose] = useState(false);
  const [showSignatureEditor, setShowSignatureEditor] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState('');
  const [savingSignature, setSavingSignature] = useState(false);
  const [composeData, setComposeData] = useState({
    to: '',
    toName: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    translateTo: '',
    translatedBody: '',
    isReply: false,
    replyToId: null,
    threadId: null,
  });
  const [sending, setSending] = useState(false);
  const [sendCountdown, setSendCountdown] = useState(0);
  const sendTimerRef = React.useRef(null);
  const sendCancelledRef = React.useRef(false);
  const [translating, setTranslating] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentScans, setAttachmentScans] = useState({}); // {filename: {safe, message, scanning}}
  const [scanningFiles, setScanningFiles] = useState(false);
  const fileInputRef = React.useRef(null);
  
  // Reply state
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [replyAttachmentScans, setReplyAttachmentScans] = useState({});
  const replyFileInputRef = React.useRef(null);
  
  // Quick replies & AI draft
  const [quickReplies, setQuickReplies] = useState([]);
  const [loadingQuickReplies, setLoadingQuickReplies] = useState(false);
  const [aiDraft, setAiDraft] = useState('');
  const [generatingDraft, setGeneratingDraft] = useState(false);
  
  // Thread summary & translation
  const [threadSummary, setThreadSummary] = useState('');
  const [summarizing, setSummarizing] = useState(false);
  const [threadTranslation, setThreadTranslation] = useState('');
  const [translatingThread, setTranslatingThread] = useState(false);
  const [translatedMessages, setTranslatedMessages] = useState({});
  const [translatingMsg, setTranslatingMsg] = useState(null);
  const [translatingReply, setTranslatingReply] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState('en');
  
  // Folder counts
  const [folderCounts, setFolderCounts] = useState({});

  // Bulk selection (checkbox per conversation + bulk actions toolbar)
  const [selectedThreadIds, setSelectedThreadIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  
  // Custom folders state
  const [customFolders, setCustomFolders] = useState([]);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState('#6366f1');
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newTemplate, setNewTemplate] = useState({ name: '', subject: '', body: '' });

  const t = language === 'es' ? {
    compose: 'Redactar',
    search: 'Buscar correo',
    noEmails: 'No hay correos en esta carpeta',
    to: 'Para',
    subject: 'Asunto',
    message: 'Mensaje',
    send: 'Enviar',
    cancel: 'Cancelar',
    reply: 'Responder',
    forward: 'Reenviar',
    archive: 'Archivar',
    delete: 'Eliminar',
    markImportant: 'Marcar importante',
    markSpam: 'Marcar spam',
    // Custom folders
    newFolder: 'Nueva carpeta',
    folderName: 'Nombre de carpeta',
    createFolder: 'Crear carpeta',
    deleteFolder: 'Eliminar carpeta',
    folderColor: 'Color',
    customFolders: 'Carpetas',
    moveTo: 'Mover a',
    refresh: 'Actualizar',
    back: 'Volver',
    translateTo: 'Traducir a',
    originalText: 'Texto original',
    translatedText: 'Texto traducido',
    translate: 'Traducir',
    useTranslation: 'Usar traducción',
    sendOriginal: 'Enviar original',
    writeReply: 'Escribe tu respuesta...',
    quickReplies: 'Respuestas rápidas',
    generateDraft: 'Generar borrador IA',
    generatingDraft: 'Generando...',
    useDraft: 'Usar borrador',
    aiLearning: 'La IA aprende de tus emails',
    you: 'Tú',
    today: 'Hoy',
    yesterday: 'Ayer',
    starred: 'Destacado',
    unstar: 'Quitar destacado',
    moveToFolder: 'Mover a carpeta',
    sent: 'Enviado',
    received: 'Recibido',
    attachments: 'Adjuntos',
    // Templates
    templates: 'Plantillas',
    newTemplate: 'Nueva plantilla',
    templateName: 'Nombre de plantilla',
    editTemplate: 'Editar plantilla',
    deleteTemplate: 'Eliminar plantilla',
    saveTemplate: 'Guardar',
    useTemplate: 'Usar plantilla',
    selectTemplate: 'Seleccionar plantilla',
    noTemplates: 'No hay plantillas guardadas',
    templateSaved: 'Plantilla guardada',
    templateDeleted: 'Plantilla eliminada',
    // Attachments
    attachFiles: 'Adjuntar archivos',
    attachedFiles: 'Archivos adjuntos',
    removeFile: 'Quitar archivo',
    maxFileSize: 'Máximo 10MB por archivo',
    fileAdded: 'Archivo añadido',
    fileTooLarge: 'El archivo es demasiado grande (máx 10MB)',
    // Virus scanning
    scanningFiles: 'Escaneando archivos con antivirus...',
    fileSafe: 'Archivo seguro',
    fileInfected: 'Archivo detectado como peligroso',
    scanComplete: 'Escaneo completado',
    virusScanEnabled: 'Protección antivirus activa (70+ antivirus)',
  } : {
    compose: 'Compose',
    search: 'Search mail',
    noEmails: 'No emails in this folder',
    to: 'To',
    subject: 'Subject',
    message: 'Message',
    send: 'Send',
    cancel: 'Cancel',
    reply: 'Reply',
    forward: 'Forward',
    archive: 'Archive',
    delete: 'Delete',
    markImportant: 'Mark important',
    markSpam: 'Mark spam',
    // Custom folders
    newFolder: 'New folder',
    folderName: 'Folder name',
    createFolder: 'Create folder',
    deleteFolder: 'Delete folder',
    folderColor: 'Color',
    customFolders: 'Folders',
    moveTo: 'Move to',
    refresh: 'Refresh',
    back: 'Back',
    translateTo: 'Translate to',
    originalText: 'Original text',
    translatedText: 'Translated text',
    translate: 'Translate',
    useTranslation: 'Use translation',
    sendOriginal: 'Send original',
    writeReply: 'Write your reply...',
    quickReplies: 'Quick replies',
    generateDraft: 'Generate AI draft',
    generatingDraft: 'Generating...',
    useDraft: 'Use draft',
    aiLearning: 'AI learns from your emails',
    you: 'You',
    today: 'Today',
    yesterday: 'Yesterday',
    starred: 'Starred',
    unstar: 'Unstar',
    moveToFolder: 'Move to folder',
    sent: 'Sent',
    received: 'Received',
    attachments: 'Attachments',
    // Templates
    templates: 'Templates',
    newTemplate: 'New template',
    templateName: 'Template name',
    editTemplate: 'Edit template',
    deleteTemplate: 'Delete template',
    saveTemplate: 'Save',
    useTemplate: 'Use template',
    selectTemplate: 'Select template',
    noTemplates: 'No saved templates',
    templateSaved: 'Template saved',
    templateDeleted: 'Template deleted',
    // Attachments
    attachFiles: 'Attach files',
    attachedFiles: 'Attached files',
    removeFile: 'Remove file',
    maxFileSize: 'Max 10MB per file',
    fileAdded: 'File added',
    fileTooLarge: 'File is too large (max 10MB)',
    // Virus scanning
    scanningFiles: 'Scanning files with antivirus...',
    fileSafe: 'File is safe',
    fileInfected: 'File detected as dangerous',
    scanComplete: 'Scan complete',
    virusScanEnabled: 'Antivirus protection active (70+ engines)',
  };

  // Track if initial sync has been done
  const initialSyncRef = useRef(false);

  // Multi-account (contact/pedidos inboxes)
  const [accounts, setAccounts] = useState([]);
  const [activeAccount, setActiveAccount] = useState('contact');
  const [switchingAccount, setSwitchingAccount] = useState(false);

  const loadAccounts = async () => {
    try {
      const res = await axios.get(`${API}/email-tool/accounts`);
      setAccounts(res.data.accounts || []);
      setActiveAccount(res.data.active || 'contact');
    } catch (e) {
      console.error('Error loading email accounts:', e);
    }
  };

  const switchAccount = async (accountId) => {
    if (accountId === activeAccount || switchingAccount) return;
    setSwitchingAccount(true);
    try {
      const res = await axios.post(`${API}/email-tool/accounts/switch/${accountId}`);
      setActiveAccount(accountId);
      toast.success(res.data.message || `Cuenta cambiada a ${res.data.email}`);
      // Reload everything for the new inbox
      initialSyncRef.current = false;
      setTimeout(() => {
        loadConversations();
        loadFolderCounts();
        loadCustomFolders();
      }, 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error cambiando cuenta');
    } finally {
      setSwitchingAccount(false);
    }
  };

  useEffect(() => { loadAccounts(); }, []);

  // Account color mapping (visual distinction between mailboxes)
  const accountColorMap = {
    contact: {
      dot: 'bg-emerald-500',
      border: 'border-l-emerald-500',
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      ring: 'ring-emerald-300',
      hex: '#10b981',
      label: 'Contact',
    },
    pedidos: {
      dot: 'bg-red-500',
      border: 'border-l-red-500',
      bg: 'bg-red-50',
      text: 'text-red-700',
      ring: 'ring-red-300',
      hex: '#ef4444',
      label: 'Pedidos',
    },
  };
  const getAccountColor = (id) => accountColorMap[id] || {
    dot: 'bg-slate-400', border: 'border-l-slate-400', bg: 'bg-slate-50',
    text: 'text-slate-700', ring: 'ring-slate-300', hex: '#64748b', label: id,
  };

  // Convert any axios error shape (string / array / object) into a readable string
  // so that toast.error never receives a non-renderable value (which crashes React → blank screen).
  const formatErr = (error, fallback = 'Error') => {
    try {
      const detail = error?.response?.data?.detail;
      if (!detail) {
        return error?.message || fallback;
      }
      if (typeof detail === 'string') return detail;
      if (Array.isArray(detail)) {
        // FastAPI 422 validation error: [{loc, msg, type}, ...]
        return detail.map(d => {
          if (typeof d === 'string') return d;
          const loc = Array.isArray(d?.loc) ? d.loc.join('.') : (d?.loc || '');
          const msg = d?.msg || JSON.stringify(d);
          return loc ? `${loc}: ${msg}` : msg;
        }).join(' | ');
      }
      if (typeof detail === 'object') {
        return detail.msg || JSON.stringify(detail);
      }
      return String(detail);
    } catch (_e) {
      return fallback;
    }
  };

  // Load conversations on mount and folder change
  useEffect(() => {
    const initEmails = async () => {
      // Only sync IMAP on first mount, not on folder changes
      if (!initialSyncRef.current) {
        initialSyncRef.current = true;
        axios.post(`${API}/email-tool/sync`).catch(() => {});
        loadCustomFolders();
        loadTemplates();
      }
      // Load from cache immediately (fast)
      loadConversations();
      loadFolderCounts();
    };
    initEmails();
    // Clear bulk selection when folder changes
    setSelectedThreadIds(new Set());
  }, [currentFolder]);

  // Auto-refresh emails every 60 seconds
  useEffect(() => {
    const autoRefresh = setInterval(async () => {
      try {
        // Sync IMAP in background
        await axios.post(`${API}/email-tool/sync`);
        // Reload data silently (no loading spinner)
        const response = await axios.get(`${API}/email-tool/conversations`, {
          params: { folder: currentFolder }
        });
        setConversations(response.data.conversations || []);
        // Update folder counts
        const countsResponse = await axios.get(`${API}/email-tool/folder-counts`);
        setFolderCounts(countsResponse.data.counts || {});
      } catch (e) {
        // Silent fail for auto-refresh
      }
    }, 60000); // Every 60 seconds

    return () => clearInterval(autoRefresh);
  }, [currentFolder]);

  // Sync emails from IMAP (only on manual refresh)
  const syncEmails = async () => {
    setConversationsLoading(true);
    try {
      // First sync IMAP to cache
      await axios.post(`${API}/email-tool/sync`);
      // Then reload conversations
      await loadConversations();
      await loadFolderCounts();
      toast.success(language === 'es' ? 'Emails sincronizados' : 'Emails synced');
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error(language === 'es' ? 'Error al sincronizar' : 'Sync error');
    } finally {
      setConversationsLoading(false);
    }
  };


  const loadSignature = async () => {
    try {
      const response = await axios.get(`${API}/email-tool/signature`);
      setSignatureData(response.data);
    } catch (err) {
      console.error('Error loading signature:', err);
    }
  };

  const loadSignaturePreview = async () => {
    try {
      const response = await axios.get(`${API}/email-tool/signature/preview`);
      setSignaturePreview(response.data.html || '');
    } catch (err) {
      console.error('Error loading signature preview:', err);
    }
  };

  const saveSignature = async () => {
    if (!signatureData) return;
    setSavingSignature(true);
    try {
      await axios.put(`${API}/email-tool/signature`, signatureData);
      toast.success(language === 'es' ? 'Firma actualizada' : 'Signature updated');
      await loadSignaturePreview();
    } catch (err) {
      toast.error(language === 'es' ? 'Error al guardar firma' : 'Error saving signature');
    } finally {
      setSavingSignature(false);
    }
  };


  const loadConversations = async () => {
    setConversationsLoading(true);
    try {
      const response = await axios.get(`${API}/email-tool/conversations`, {
        params: { folder: currentFolder }
      });
      const convos = response.data.conversations || [];
      setConversations(convos);
      return convos;
    } catch (error) {
      console.error('Error loading conversations:', error);
      setConversations([]);
    } finally {
      setConversationsLoading(false);
    }
  };

  const loadFolderCounts = async () => {
    try {
      const response = await axios.get(`${API}/email-tool/folder-counts`);
      setFolderCounts(response.data.counts || {});
    } catch (error) {
      // Silent fail for counts
    }
  };

  // Load custom folders
  const loadCustomFolders = async () => {
    try {
      const response = await axios.get(`${API}/email-tool/custom-folders`);
      setCustomFolders(response.data.folders || []);
    } catch (error) {
      console.error('Error loading custom folders:', error);
    }
  };

  // Create custom folder
  const createFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error(language === 'es' ? 'Ingresa un nombre' : 'Enter a name');
      return;
    }
    
    try {
      await axios.post(`${API}/email-tool/custom-folders`, {
        name: newFolderName.trim(),
        color: newFolderColor
      });
      toast.success(language === 'es' ? 'Carpeta creada' : 'Folder created');
      setNewFolderName('');
      setShowCreateFolder(false);
      loadCustomFolders();
      loadFolderCounts();
    } catch (error) {
      toast.error(language === 'es' ? 'Error creando carpeta' : 'Error creating folder');
    }
  };

  // Delete custom folder
  const deleteCustomFolder = async (folderId) => {
    if (!window.confirm(language === 'es' 
      ? '¿Eliminar esta carpeta? Los correos se moverán a Recibidos.' 
      : 'Delete this folder? Emails will be moved to Inbox.'
    )) return;
    try {
      await axios.delete(`${API}/email-tool/custom-folders/${folderId}`);
      toast.success(language === 'es' ? 'Carpeta eliminada' : 'Folder deleted');
      loadCustomFolders();
      loadFolderCounts();
      if (currentFolder === `custom_${folderId}`) {
        setCurrentFolder('inbox');
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error eliminando' : 'Error deleting');
    }
  };

  // Load templates
  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API}/email-tool/templates`);
      // API returns array directly or object with templates property
      const templatesData = Array.isArray(response.data) ? response.data : (response.data.templates || []);
      setTemplates(templatesData);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  // Export templates as JSON
  const exportTemplatesJson = async () => {
    try {
      const response = await axios.get(`${API}/email-tool/templates/export/json`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '').slice(0, 15);
      a.href = url;
      a.download = `email_templates_${stamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(language === 'es' ? `${response.data.count || 0} plantillas exportadas` : `${response.data.count || 0} templates exported`);
    } catch (error) {
      toast.error(language === 'es' ? 'Error exportando plantillas' : 'Error exporting templates');
    }
  };

  // Import templates from JSON
  const importTemplatesJson = async (file) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/email-tool/templates/import/json`, formData);
      const { created = 0, updated = 0, errors = [] } = response.data;
      toast.success(language === 'es'
        ? `Importación OK: ${created} creadas, ${updated} actualizadas`
        : `Import OK: ${created} created, ${updated} updated`);
      if (errors.length > 0) toast.warning(`${errors.length} errores`);
      loadTemplates();
    } catch (error) {
      toast.error(formatErr(error, language === 'es' ? 'Error importando' : 'Error importing'));
    }
  };

  // Export custom folders as JSON
  const exportFoldersJson = async () => {
    try {
      const response = await axios.get(`${API}/email-tool/custom-folders/export/json`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '').slice(0, 15);
      a.href = url;
      a.download = `email_folders_${stamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(language === 'es' ? `${response.data.count || 0} carpetas exportadas` : `${response.data.count || 0} folders exported`);
    } catch (error) {
      toast.error(language === 'es' ? 'Error exportando carpetas' : 'Error exporting folders');
    }
  };

  // Import folders from JSON
  const importFoldersJson = async (file) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/email-tool/custom-folders/import/json`, formData);
      const { created = 0, updated = 0, errors = [] } = response.data;
      toast.success(language === 'es'
        ? `Importación OK: ${created} creadas, ${updated} actualizadas`
        : `Import OK: ${created} created, ${updated} updated`);
      if (errors.length > 0) toast.warning(`${errors.length} errores`);
      loadCustomFolders();
    } catch (error) {
      toast.error(formatErr(error, language === 'es' ? 'Error importando' : 'Error importing'));
    }
  };

  // Save template
  const saveTemplate = async () => {
    const bodyClean = newTemplate.body.replace(/<[^>]+>/g, '').trim();
    if (!newTemplate.name.trim() || !newTemplate.subject.trim() || !bodyClean) {
      toast.error(language === 'es' ? 'Completa todos los campos' : 'Fill all fields');
      return;
    }
    
    try {
      // Backend expects body_text, not body
      const templateData = {
        name: newTemplate.name,
        subject: newTemplate.subject,
        body_text: newTemplate.body,
        description: newTemplate.description || ''
      };
      
      if (editingTemplate) {
        await axios.put(`${API}/email-tool/templates/${editingTemplate.id}`, templateData);
      } else {
        await axios.post(`${API}/email-tool/templates`, templateData);
      }
      toast.success(t.templateSaved);
      setNewTemplate({ name: '', subject: '', body: '' });
      setEditingTemplate(null);
      loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(language === 'es' ? 'Error guardando plantilla' : 'Error saving template');
    }
  };

  // Delete template
  const deleteTemplate = async (id) => {
    if (!window.confirm(language === 'es' 
      ? '¿Eliminar esta plantilla?' 
      : 'Delete this template?'
    )) return;
    try {
      await axios.delete(`${API}/email-tool/templates/${id}`);
      toast.success(t.templateDeleted);
      loadTemplates();
    } catch (error) {
      toast.error(language === 'es' ? 'Error eliminando' : 'Error deleting');
    }
  };

  // Apply template to compose
  const applyTemplate = (template) => {
    setComposeData(prev => ({
      ...prev,
      subject: template.subject,
      body: template.body_text || template.body,
      translatedBody: '',
    }));
    setShowTemplates(false);
    if (!showCompose) setShowCompose(true);
  };

  // Open conversation detail
  const openConversation = (conv) => {
    setSelectedConversation(conv);
    setReplyText('');
    setQuickReplies([]);
    setAiDraft('');
    setThreadSummary('');
    setThreadTranslation('');
    setTranslatedMessages({});
    
    // Detect language of the email
    const lastReceived = [...conv.messages].reverse().find(m => m.type === 'received');
    if (lastReceived) {
      const detected = detectEmailLanguage(lastReceived.body);
      setDetectedLanguage(detected);
      loadQuickReplies(lastReceived, conv.subject);
    } else {
      setDetectedLanguage('en');
    }
    
    // Get IDs of received emails to mark as read
    const receivedEmailIds = conv.messages
      .filter(m => m.type === 'received')
      .map(m => m.id);
    
    // Mark as read
    markConversationRead(conv.thread_id, receivedEmailIds);
  };

  // Clean quoted replies from email body (for display only)
  const cleanQuotedContent = (text) => {
    if (!text) return '';
    
    // For HTML content, look for common quoted reply markers and remove them
    // Gmail: <div class="gmail_quote">
    // Outlook: <div id="appendonsend">
    // Generic: <blockquote>
    let cleaned = text;
    
    // Remove Gmail quoted blocks
    cleaned = cleaned.replace(/<div[^>]*class="gmail_quote"[^>]*>[\s\S]*$/i, '');
    
    // Remove Outlook quoted blocks
    cleaned = cleaned.replace(/<div[^>]*id="appendonsend"[^>]*>[\s\S]*$/i, '');
    cleaned = cleaned.replace(/<div[^>]*id="divRplyFwdMsg"[^>]*>[\s\S]*$/i, '');
    
    // Remove <blockquote> sections (quoted replies)
    cleaned = cleaned.replace(/<blockquote[^>]*>[\s\S]*?<\/blockquote>/gi, '');
    
    // For plain text: remove lines starting with ">"
    if (!text.includes('<')) {
      cleaned = cleaned.split('\n').filter(line => !line.trim().startsWith('>')).join('\n');
      // Remove "On ... wrote:" line and everything after (only for plain text)
      cleaned = cleaned.replace(/\n\s*On\s+.*?wrote:\s*\n[\s\S]*$/i, '');
      cleaned = cleaned.replace(/\n\s*El\s+[a-záéíóú]+,?\s+\d+.*?escribi[oó]:\s*\n[\s\S]*$/i, '');
    }
    
    // Remove excessive whitespace
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();
    
    return cleaned;
  };

  // Detect language from text
  const detectEmailLanguage = (text) => {
    if (!text) return 'en';
    const lowerText = text.toLowerCase();
    
    // Simple language detection based on common words
    if (/\b(hola|gracias|saludos|buenos|días|precio|quiero|nosotros|también)\b/.test(lowerText)) return 'es';
    if (/\b(guten|danke|bitte|preis|sehr|grüße|kooperation|freundliche|vielen)\b/.test(lowerText)) return 'de';
    if (/\b(bonjour|merci|prix|salut|cordialement|nous|vous|être)\b/.test(lowerText)) return 'fr';
    if (/\b(ciao|grazie|buongiorno|prezzo|saluti|prego|sono)\b/.test(lowerText)) return 'it';
    if (/\b(olá|obrigado|preço|bom dia|você|nós)\b/.test(lowerText)) return 'pt';
    return 'en'; // Default to English
  };

  const markConversationRead = async (threadId, emailIds = []) => {
    try {
      await axios.put(`${API}/email-tool/conversations/${encodeURIComponent(threadId)}/read`, emailIds);
      // Update local state to reflect read status
      loadFolderCounts();
    } catch (error) {
      // Silent fail
    }
  };

  const markConversationUnread = async () => {
    if (!selectedConversation) return;
    try {
      const receivedIds = selectedConversation.messages
        .filter(m => m.type === 'received')
        .map(m => m.id);
      await axios.put(`${API}/email-tool/conversations/${encodeURIComponent(selectedConversation.thread_id)}/unread`, receivedIds);
      toast.success(language === 'es' ? 'Marcado como no leído' : 'Marked as unread');
      setSelectedConversation(null);
      loadFolderCounts();
      loadConversations();
    } catch (error) {
      toast.error(language === 'es' ? 'Error al marcar como no leído' : 'Error marking as unread');
    }
  };


  const loadQuickReplies = async (lastMessage, subject) => {
    setLoadingQuickReplies(true);
    try {
      const response = await axios.post(`${API}/email-tool/quick-replies`, {
        last_message: lastMessage.body,
        sender_name: lastMessage.from_name || lastMessage.from_email,
        subject: subject,
        language: language
      });
      setQuickReplies(response.data.suggestions || []);
    } catch (error) {
      setQuickReplies([]);
    } finally {
      setLoadingQuickReplies(false);
    }
  };

  const generateAIDraft = async () => {
    if (!selectedConversation) return;
    setGeneratingDraft(true);
    setAiDraft('');
    
    try {
      const conversationContext = selectedConversation.messages.map(msg => {
        const sender = msg.type === 'sent' ? 'Tú' : msg.from_name;
        return `[${new Date(msg.date).toLocaleString('es-ES')}] ${sender}:\n${msg.body}`;
      }).join('\n\n---\n\n');
      
      const lastMessage = selectedConversation.messages[selectedConversation.messages.length - 1];
      
      const response = await axios.post(`${API}/email-tool/generate-draft`, {
        conversation_context: conversationContext,
        last_message: lastMessage.body,
        last_sender_name: lastMessage.from_name || lastMessage.from_email,
        contact_email: selectedConversation.contact_email,
        subject: selectedConversation.subject
      });
      
      if (response.data.success) {
        setAiDraft(response.data.draft);
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error generando borrador' : 'Error generating draft');
    } finally {
      setGeneratingDraft(false);
    }
  };

  // Summarize thread
  const summarizeThread = async () => {
    if (!selectedConversation) return;
    setSummarizing(true);
    setThreadSummary('');
    
    try {
      const conversationText = selectedConversation.messages.map(msg => {
        const sender = msg.type === 'sent' ? 'Yo' : msg.from_name;
        return `${sender}: ${msg.body}`;
      }).join('\n\n');
      
      const response = await axios.post(`${API}/email-tool/summarize`, {
        email_thread: conversationText,
        target_language: language === 'es' ? 'español' : 'english'
      });
      
      if (response.data.result) {
        setThreadSummary(response.data.result);
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error resumiendo' : 'Error summarizing');
    } finally {
      setSummarizing(false);
    }
  };

  // Translate entire thread to Spanish
  const translateThread = async () => {
    if (!selectedConversation) return;
    setTranslatingThread(true);
    
    try {
      const receivedMessages = selectedConversation.messages.filter(msg => msg.type === 'received');
      
      if (!receivedMessages.length) {
        toast.info('No hay mensajes recibidos para traducir');
        return;
      }
      
      const newTranslations = {};
      for (const msg of receivedMessages) {
        try {
          // Strip HTML tags before translating
          const plainText = (msg.body_html || msg.body || '')
            .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
            .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
            .replace(/<[^>]+>/g, ' ')
            .replace(/&nbsp;/g, ' ')
            .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')
            .replace(/\s{2,}/g, ' ')
            .trim();
          
          if (!plainText) continue;
          
          const response = await axios.post(`${API}/email-tool/translate`, {
            text: plainText,
            target_language: 'español'
          });
          if (response.data.result) {
            newTranslations[msg.id] = response.data.result;
          }
        } catch {
          // Skip failed translations
        }
      }
      
      setTranslatedMessages(newTranslations);
      toast.success(`${Object.keys(newTranslations).length} mensajes traducidos`);
    } catch (error) {
      toast.error('Error traduciendo');
    } finally {
      setTranslatingThread(false);
    }
  };

  // Translate a single message - always to Spanish
  const translateMessage = async (msgId, text) => {
    setTranslatingMsg(msgId);
    
    try {
      const response = await axios.post(`${API}/email-tool/translate`, {
        text: text,
        target_language: 'español'
      });
      
      if (response.data.result) {
        setTranslatedMessages(prev => ({
          ...prev,
          [msgId]: response.data.result
        }));
      }
    } catch (error) {
      toast.error('Error traduciendo');
    } finally {
      setTranslatingMsg(null);
    }
  };

  // Get language name for display
  const getLanguageName = (code) => {
    const names = {
      'es': 'Español',
      'en': 'Inglés',
      'de': 'Alemán',
      'fr': 'Francés',
      'it': 'Italiano',
      'pt': 'Portugués'
    };
    return names[code] || code;
  };

  // Translate reply to target language
  const translateReplyToLanguage = async (targetLang) => {
    if (!replyText.trim()) return;
    
    setTranslatingReply(true);
    try {
      const response = await axios.post(`${API}/email-tool/translate`, {
        text: replyText,
        target_language: getLanguageName(targetLang)
      });
      
      if (response.data.result) {
        setReplyText(response.data.result);
        toast.success(`Traducido a ${getLanguageName(targetLang)}`);
      }
    } catch (error) {
      toast.error('Error traduciendo');
    } finally {
      setTranslatingReply(false);
    }
  };

  // Apply template and auto-translate if needed
  const applyTemplateAndTranslate = async (templateText) => {
    setReplyText(templateText);
    // Auto-translate if contact speaks a different language
    if (detectedLanguage && detectedLanguage !== 'es') {
      setTranslatingReply(true);
      try {
        const response = await axios.post(`${API}/email-tool/translate`, {
          text: templateText,
          target_language: getLanguageName(detectedLanguage)
        });
        if (response.data.result) {
          setReplyText(response.data.result);
          toast.success(`Plantilla traducida a ${getLanguageName(detectedLanguage)}`);
        }
      } catch (error) {
        toast.error('Plantilla insertada (error al traducir)');
      } finally {
        setTranslatingReply(false);
      }
    }
  };

  // Send reply
  const sendReply = async () => {
    const cleanText = replyText.replace(/<[^>]+>/g, '').trim();
    if (!cleanText || !selectedConversation) return;
    
    setSendingReply(true);
    try {
      // Use FormData if there are attachments
      if (replyAttachments.length > 0) {
        const formData = new FormData();
        formData.append('to_email', selectedConversation.contact_email);
        formData.append('to_name', selectedConversation.contact_name || '');
        formData.append('subject', `Re: ${selectedConversation.subject}`);
        formData.append('body_text', replyText);
        formData.append('is_reply', 'true');
        
        replyAttachments.forEach(file => {
          formData.append('files', file);
        });
        
        await axios.post(`${API}/email-tool/send-with-attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${API}/email-tool/send`, {
          to_email: selectedConversation.contact_email,
          to_name: selectedConversation.contact_name,
          subject: `Re: ${selectedConversation.subject}`,
          body_text: replyText,
          thread_id: selectedConversation.thread_id,
        });
      }
      
      toast.success(language === 'es' ? 'Respuesta enviada' : 'Reply sent');
      setReplyText('');
      setAiDraft('');
      setReplyAttachments([]);
      
      // Reload and update the selected conversation
      const convos = await loadConversations();
      loadFolderCounts();
      
      // Re-select the same conversation with updated messages
      if (selectedConversation && convos) {
        const updated = convos.find(c => c.thread_id === selectedConversation.thread_id);
        if (updated) {
          setSelectedConversation(updated);
        }
      }
      
    } catch (error) {
      toast.error(formatErr(error, language === 'es' ? 'Error enviando' : 'Error sending'));
    } finally {
      setSendingReply(false);
    }
  };

  // Translate compose text
  const translateText = async () => {
    if (!composeData.body.trim() || !composeData.translateTo) return;
    
    setTranslating(true);
    try {
      const response = await axios.post(`${API}/email-tool/translate`, {
        text: composeData.body,
        target_language: composeData.translateTo
      });
      
      if (response.data.success) {
        setComposeData(prev => ({
          ...prev,
          translatedBody: response.data.result
        }));
        toast.success(language === 'es' ? 'Traducido' : 'Translated');
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error traduciendo' : 'Error translating');
    } finally {
      setTranslating(false);
    }
  };

  // Handle file selection for compose (with virus scan)
  const handleFileSelect = async (e, isReply = false) => {
    const files = Array.from(e.target.files);
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        toast.error(`${file.name}: ${t.fileTooLarge}`);
        return false;
      }
      return true;
    });
    
    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }
    
    // Add files first with "scanning" status
    if (isReply) {
      setReplyAttachments(prev => [...prev, ...validFiles]);
      validFiles.forEach(f => {
        setReplyAttachmentScans(prev => ({
          ...prev,
          [f.name]: { scanning: true, safe: null, message: t.scanningFiles }
        }));
      });
    } else {
      setAttachments(prev => [...prev, ...validFiles]);
      validFiles.forEach(f => {
        setAttachmentScans(prev => ({
          ...prev,
          [f.name]: { scanning: true, safe: null, message: t.scanningFiles }
        }));
      });
    }
    
    // Scan files with VirusTotal
    setScanningFiles(true);
    try {
      const formData = new FormData();
      validFiles.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await axios.post(`${API}/email-tool/scan-files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000 // 60 second timeout for scanning
      });
      
      if (response.data.success) {
        const scanResults = {};
        response.data.results.forEach(result => {
          scanResults[result.filename] = {
            scanning: false,
            safe: result.safe,
            message: result.message
          };
          
          // Show toast for each file
          if (result.safe) {
            toast.success(`${result.filename}: ${result.message}`);
          } else {
            toast.error(`${result.filename}: ${result.message}`);
          }
        });
        
        if (isReply) {
          setReplyAttachmentScans(prev => ({ ...prev, ...scanResults }));
          // Remove unsafe files
          if (!response.data.all_safe) {
            const unsafeFiles = response.data.results.filter(r => !r.safe).map(r => r.filename);
            setReplyAttachments(prev => prev.filter(f => !unsafeFiles.includes(f.name)));
          }
        } else {
          setAttachmentScans(prev => ({ ...prev, ...scanResults }));
          // Remove unsafe files
          if (!response.data.all_safe) {
            const unsafeFiles = response.data.results.filter(r => !r.safe).map(r => r.filename);
            setAttachments(prev => prev.filter(f => !unsafeFiles.includes(f.name)));
          }
        }
      }
    } catch (error) {
      console.error('Scan error:', error);
      // Mark files as "scan failed" but allow them
      validFiles.forEach(f => {
        const scanData = {
          scanning: false,
          safe: true,
          message: '⚠️ No se pudo escanear'
        };
        if (isReply) {
          setReplyAttachmentScans(prev => ({ ...prev, [f.name]: scanData }));
        } else {
          setAttachmentScans(prev => ({ ...prev, [f.name]: scanData }));
        }
      });
      toast.warning(language === 'es' ? 'Escaneo antivirus no disponible' : 'Virus scan unavailable');
    } finally {
      setScanningFiles(false);
    }
    
    // Reset input
    e.target.value = '';
  };

  // Remove attachment
  const removeAttachment = (index, isReply = false) => {
    if (isReply) {
      const fileName = replyAttachments[index]?.name;
      setReplyAttachments(prev => prev.filter((_, i) => i !== index));
      if (fileName) {
        setReplyAttachmentScans(prev => {
          const newScans = { ...prev };
          delete newScans[fileName];
          return newScans;
        });
      }
    } else {
      const fileName = attachments[index]?.name;
      setAttachments(prev => prev.filter((_, i) => i !== index));
      if (fileName) {
        setAttachmentScans(prev => {
          const newScans = { ...prev };
          delete newScans[fileName];
          return newScans;
        });
      }
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Duplicate check state — popup before sending if recipient(s) already received same subject
  const [dupCheckLoading, setDupCheckLoading] = useState(false);
  const [dupModal, setDupModal] = useState(null); // { duplicates: [...], bodyToSend: string }

  // Helper: parse "a@x.com; b@y.com, c@z.com" → ["a@x.com","b@y.com","c@z.com"]
  const _parseEmails = (raw) => {
    if (!raw) return [];
    return String(raw)
      .split(/[;,\s]+/)
      .map(s => s.trim().toLowerCase())
      .filter(Boolean);
  };

  // Send new email (updated with attachments)
  const sendEmail = async (useTranslation = false) => {
    const bodyToSend = useTranslation && composeData.translatedBody 
      ? composeData.translatedBody 
      : composeData.body;
    
    if (!composeData.to || !composeData.subject || !bodyToSend) {
      toast.error(language === 'es' ? 'Completa todos los campos' : 'Fill all fields');
      return;
    }

    // 1) Check duplicates BEFORE starting the 5s countdown
    try {
      setDupCheckLoading(true);
      const recipients = _parseEmails(composeData.to);
      if (recipients.length > 0) {
        const res = await axios.post(`${API}/email-tool/check-duplicate`, {
          to_emails: recipients,
          subject: composeData.subject,
        });
        const dups = (res.data && res.data.duplicates) || [];
        if (dups.length > 0) {
          // Show modal — let the user confirm before sending
          setDupModal({ duplicates: dups, bodyToSend });
          setDupCheckLoading(false);
          return;
        }
      }
    } catch (err) {
      // If the duplicate check fails, don't block the user — log and continue
      console.warn('Duplicate check failed (non-blocking):', err);
    } finally {
      setDupCheckLoading(false);
    }

    _startSendCountdown(bodyToSend);
  };

  // Extracted countdown so we can reuse it from the duplicate-confirm modal
  const _startSendCountdown = (bodyToSend) => {
    sendCancelledRef.current = false;
    setSendCountdown(5);
    
    let countdown = 5;
    sendTimerRef.current = setInterval(() => {
      countdown -= 1;
      setSendCountdown(countdown);
      if (countdown <= 0) {
        clearInterval(sendTimerRef.current);
        sendTimerRef.current = null;
        if (!sendCancelledRef.current) {
          executeSend(bodyToSend);
        }
      }
    }, 1000);
  };

  const cancelSend = () => {
    sendCancelledRef.current = true;
    if (sendTimerRef.current) {
      clearInterval(sendTimerRef.current);
      sendTimerRef.current = null;
    }
    setSendCountdown(0);
    toast.info(language === 'es' ? 'Envío cancelado' : 'Send cancelled');
  };

  const executeSend = async (bodyToSend) => {
    setSending(true);
    setSendCountdown(0);
    try {
      if (attachments.length > 0) {
        const formData = new FormData();
        formData.append('to_email', composeData.to);
        formData.append('to_name', composeData.toName || '');
        formData.append('subject', composeData.subject);
        formData.append('body_text', bodyToSend);
        formData.append('is_reply', 'false');
        if (composeData.cc) formData.append('cc', composeData.cc);
        if (composeData.bcc) formData.append('bcc', composeData.bcc);
        
        attachments.forEach(file => {
          formData.append('files', file);
        });
        
        await axios.post(`${API}/email-tool/send-with-attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${API}/email-tool/send`, {
          to_email: composeData.to,
          to_name: composeData.toName,
          subject: composeData.subject,
          body_text: bodyToSend,
          cc: composeData.cc || undefined,
          bcc: composeData.bcc || undefined,
        });
      }
      
      toast.success(language === 'es' ? 'Email enviado' : 'Email sent');
      setShowCompose(false);
      resetCompose();
      loadConversations();
    } catch (error) {
      toast.error(formatErr(error, language === 'es' ? 'Error enviando' : 'Error sending'));
    } finally {
      setSending(false);
    }
  };

  const resetCompose = () => {
    setComposeData({
      to: '',
      toName: '',
      subject: '',
      body: '',
      translateTo: '',
      translatedBody: '',
      isReply: false,
      replyToId: null,
      threadId: null,
    });
    setAttachments([]);
    setAttachmentScans({});
  };

  // Move to folder
  const moveToFolder = async (threadId, folder) => {
    try {
      await axios.put(`${API}/email-tool/conversations/${encodeURIComponent(threadId)}/folder`, { folder });
      toast.success(language === 'es' ? 'Movido' : 'Moved');
      loadConversations();
      if (selectedConversation?.thread_id === threadId) {
        setSelectedConversation(null);
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error moviendo' : 'Error moving');
    }
  };

  // ===== BULK SELECTION HELPERS =====
  const toggleSelectThread = (threadId) => {
    setSelectedThreadIds(prev => {
      const next = new Set(prev);
      if (next.has(threadId)) next.delete(threadId);
      else next.add(threadId);
      return next;
    });
  };

  const clearSelection = () => setSelectedThreadIds(new Set());

  const selectAllVisible = (visibleThreadIds) => {
    setSelectedThreadIds(prev => {
      const next = new Set(prev);
      visibleThreadIds.forEach(id => next.add(id));
      return next;
    });
  };

  const isAllVisibleSelected = (visibleThreadIds) => {
    if (visibleThreadIds.length === 0) return false;
    return visibleThreadIds.every(id => selectedThreadIds.has(id));
  };

  const bulkMoveToFolder = async (folder) => {
    if (selectedThreadIds.size === 0) return;
    const ids = Array.from(selectedThreadIds);
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(
        ids.map(id => axios.put(`${API}/email-tool/conversations/${encodeURIComponent(id)}/folder`, { folder }))
      );
      const ok = results.filter(r => r.status === 'fulfilled').length;
      const fail = results.length - ok;
      toast.success(language === 'es'
        ? `${ok} movidos${fail ? ` · ${fail} fallaron` : ''}`
        : `${ok} moved${fail ? ` · ${fail} failed` : ''}`
      );
      clearSelection();
      loadConversations();
      loadFolderCounts();
      if (selectedConversation && ids.includes(selectedConversation.thread_id)) {
        setSelectedConversation(null);
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error en acción masiva' : 'Bulk action failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDelete = async () => {
    if (selectedThreadIds.size === 0) return;
    const permanent = currentFolder === 'trash';
    const confirmMsg = permanent
      ? (language === 'es'
          ? `¿Eliminar permanentemente ${selectedThreadIds.size} conversaciones? Esta acción no se puede deshacer.`
          : `Permanently delete ${selectedThreadIds.size} conversations? This cannot be undone.`)
      : (language === 'es'
          ? `¿Mover ${selectedThreadIds.size} conversaciones a la papelera?`
          : `Move ${selectedThreadIds.size} conversations to trash?`);
    if (!window.confirm(confirmMsg)) return;
    const ids = Array.from(selectedThreadIds);
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(
        ids.map(id => axios.delete(`${API}/email-tool/conversations/${encodeURIComponent(id)}`, {
          params: { permanent: permanent ? 'true' : 'false' }
        }))
      );
      const ok = results.filter(r => r.status === 'fulfilled').length;
      const fail = results.length - ok;
      toast.success(language === 'es'
        ? `${ok} eliminadas${fail ? ` · ${fail} fallaron` : ''}`
        : `${ok} deleted${fail ? ` · ${fail} failed` : ''}`
      );
      clearSelection();
      loadConversations();
      loadFolderCounts();
      if (selectedConversation && ids.includes(selectedConversation.thread_id)) {
        setSelectedConversation(null);
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error eliminando' : 'Error deleting');
    } finally {
      setBulkBusy(false);
    }
  };
  // ===== END BULK SELECTION HELPERS =====

  // Toggle starred
  // Star colors configuration
  const starColors = [
    { id: 'yellow', color: 'text-amber-400', fill: 'fill-amber-400', label: { es: 'Amarillo', en: 'Yellow' } },
    { id: 'red', color: 'text-red-500', fill: 'fill-red-500', label: { es: 'Rojo', en: 'Red' } },
    { id: 'blue', color: 'text-blue-500', fill: 'fill-blue-500', label: { es: 'Azul', en: 'Blue' } },
    { id: 'green', color: 'text-green-500', fill: 'fill-green-500', label: { es: 'Verde', en: 'Green' } },
    { id: 'purple', color: 'text-purple-500', fill: 'fill-purple-500', label: { es: 'Morado', en: 'Purple' } },
    { id: 'orange', color: 'text-orange-500', fill: 'fill-orange-500', label: { es: 'Naranja', en: 'Orange' } },
  ];

  const getStarColor = (starColor) => {
    const found = starColors.find(s => s.id === starColor);
    return found || starColors[0]; // Default to yellow
  };

  // Export full email backup
  const exportFullBackup = async () => {
    try {
      toast.info(language === 'es' ? 'Generando copia completa...' : 'Generating full backup...');
      const response = await axios.get(`${API}/email-tool/backup/export`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '').slice(0, 15);
      a.href = url;
      a.download = `email_backup_${stamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
      const counts = response.data.counts || {};
      const total = Object.values(counts).reduce((a, b) => a + b, 0);
      toast.success(language === 'es' ? `Copia completa descargada (${total} elementos)` : `Full backup downloaded (${total} items)`);
    } catch (error) {
      toast.error(language === 'es' ? 'Error generando copia' : 'Error generating backup');
    }
  };

  // Import full email backup
  const importFullBackup = async (file) => {
    if (!file) return;
    const confirmMsg = language === 'es'
      ? '¿Importar copia completa de email? Se añadirá sin borrar nada existente.'
      : 'Import full email backup? Will be added without deleting anything existing.';
    if (!window.confirm(confirmMsg)) return;
    try {
      toast.info(language === 'es' ? 'Importando... esto puede tardar' : 'Importing... this may take a while');
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/email-tool/backup/import`, formData);
      const summary = response.data.summary || {};
      const totalCreated = Object.values(summary).reduce((a, b) => a + (b.created || 0), 0);
      const totalUpdated = Object.values(summary).reduce((a, b) => a + (b.updated || 0), 0);
      toast.success(language === 'es'
        ? `Restauración OK: ${totalCreated} nuevos, ${totalUpdated} actualizados`
        : `Restore OK: ${totalCreated} new, ${totalUpdated} updated`);
      if ((response.data.errors || []).length > 0) {
        toast.warning(`${response.data.errors.length} errores (revisa consola)`);
        console.warn('Import errors:', response.data.errors);
      }
      loadTemplates();
      loadCustomFolders();
      if (typeof loadConversations === 'function') loadConversations();
    } catch (error) {
      toast.error(formatErr(error, language === 'es' ? 'Error importando' : 'Error importing'));
    }
  };

  const toggleStarred = async (threadId, currentStar, newColor = null) => {
    try {
      // If clicking on already starred with same color, remove star
      // If clicking with new color, update color
      const shouldRemove = currentStar && !newColor;
      const starValue = shouldRemove ? null : (newColor || 'yellow');
      
      await axios.put(`${API}/email-tool/conversations/${encodeURIComponent(threadId)}/star`, { 
        starred: !shouldRemove,
        star_color: starValue
      });
      loadConversations();
    } catch (error) {
      // Silent fail
    }
  };

  // Delete conversation
  const deleteConversation = async (threadId, permanent = false) => {
    const confirmMsg = permanent 
      ? (language === 'es' ? '¿Eliminar permanentemente este correo? Esta acción no se puede deshacer.' : 'Permanently delete this email? This action cannot be undone.')
      : (language === 'es' ? '¿Mover este correo a la papelera?' : 'Move this email to trash?');
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.delete(`${API}/email-tool/conversations/${encodeURIComponent(threadId)}`, {
        params: { permanent }
      });
      toast.success(language === 'es' 
        ? (permanent ? 'Eliminado permanentemente' : 'Movido a papelera') 
        : (permanent ? 'Permanently deleted' : 'Moved to trash')
      );
      loadConversations();
      loadFolderCounts();
      if (selectedConversation?.thread_id === threadId) {
        setSelectedConversation(null);
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error al eliminar' : 'Error deleting');
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    
    // Try to parse the date
    let date;
    try {
      date = new Date(dateStr);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return '';
      }
    } catch (e) {
      return '';
    }
    
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return t.yesterday;
    } else if (diffDays < 7) {
      return date.toLocaleDateString('es-ES', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
    }
  };

  // Filter conversations by search
  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      conv.subject?.toLowerCase().includes(query) ||
      conv.contact_name?.toLowerCase().includes(query) ||
      conv.contact_email?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Top bar with close button */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-slate-50 shrink-0">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-blue-600" />
          <span className="font-semibold text-sm">{language === 'es' ? 'Correo' : 'Email'}</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            window.dispatchEvent(new CustomEvent('closeEmailTool'));
          }}
          data-testid="close-email-btn"
          className="text-slate-500 hover:text-slate-700"
        >
          <X className="h-4 w-4 mr-1" />
          {language === 'es' ? 'Cerrar' : 'Close'}
        </Button>
      </div>

      {/* Email client */}
      <div className="flex flex-1 overflow-hidden">
      {/* ========== SIDEBAR ========== */}
      <div className="w-48 lg:w-56 border-r bg-slate-50 flex flex-col shrink-0">
        {/* Compose Button */}
        <div className="p-4 space-y-2">
          {/* Account switcher */}
          {accounts.length > 1 && (
            <div className="mb-2">
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                {language === 'es' ? 'Cuenta activa' : 'Active account'}
              </label>
              <div className={`mt-1 flex items-center gap-2 px-2 py-1.5 border-2 rounded-md bg-white ${getAccountColor(activeAccount).ring} ring-2 ring-offset-0`}
                   style={{ borderColor: getAccountColor(activeAccount).hex }}
                   data-testid="email-account-switcher-wrapper">
                <span className={`inline-block h-3 w-3 rounded-full ${getAccountColor(activeAccount).dot} flex-shrink-0`}
                      data-testid={`account-color-dot-${activeAccount}`} />
                <select
                  value={activeAccount}
                  onChange={(e) => switchAccount(e.target.value)}
                  disabled={switchingAccount}
                  className={`flex-1 text-sm bg-transparent focus:outline-none font-semibold ${getAccountColor(activeAccount).text}`}
                  data-testid="email-account-switcher"
                >
                  {accounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.email}
                    </option>
                  ))}
                </select>
              </div>
              {switchingAccount && (
                <div className="text-[10px] text-slate-400 mt-1">
                  {language === 'es' ? 'Cambiando...' : 'Switching...'}
                </div>
              )}
            </div>
          )}

          <Button
            onClick={() => setShowCompose(true)}
            className="w-full bg-blue-100 hover:bg-blue-200 text-blue-700 border border-blue-200 shadow-sm"
          >
            <Edit3 className="h-4 w-4 mr-2" />
            {t.compose}
          </Button>
          <Button
            onClick={() => { loadSignature(); loadSignaturePreview(); setShowSignatureEditor(true); }}
            variant="ghost"
            className="w-full text-xs text-slate-500 hover:text-slate-700"
            data-testid="edit-signature-btn"
          >
            <Settings className="h-3 w-3 mr-1" />
            {language === 'es' ? 'Editar Firma' : 'Edit Signature'}
          </Button>
          {/* Full backup buttons */}
          <div className="flex gap-1 pt-1">
            <Button
              onClick={exportFullBackup}
              variant="outline"
              size="sm"
              className="flex-1 text-[11px] h-8"
              title={language === 'es' ? 'Exportar Todo el Email (JSON)' : 'Export All Email (JSON)'}
              data-testid="email-backup-export-btn"
            >
              <Download className="h-3 w-3 mr-1" />
              {language === 'es' ? 'Exportar Todo' : 'Export All'}
            </Button>
            <label
              className="flex-1 inline-flex items-center justify-center text-[11px] h-8 px-2 rounded-md border bg-background hover:bg-accent cursor-pointer"
              title={language === 'es' ? 'Importar copia completa (JSON)' : 'Import full backup (JSON)'}
              data-testid="email-backup-import-btn"
            >
              <Upload className="h-3 w-3 mr-1" />
              {language === 'es' ? 'Importar Todo' : 'Import All'}
              <input
                type="file"
                accept=".json,application/json"
                className="hidden"
                onChange={(e) => { importFullBackup(e.target.files?.[0]); e.target.value = ''; }}
              />
            </label>
          </div>
        </div>
        
        {/* Folders */}
        <nav className="flex-1 px-2 space-y-1 overflow-y-auto">
          {Object.entries(FOLDERS).map(([key, folder]) => {
            const Icon = folder.icon;
            const isActive = currentFolder === key && !showTemplates;
            const count = folderCounts[key] || 0;
            
            return (
              <button
                key={key}
                onClick={() => {
                  setCurrentFolder(key);
                  setEmailPage(1);
                  setSelectedConversation(null);
                  setShowTemplates(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                <span className="flex items-center gap-3">
                  <Icon className={`h-4 w-4 ${folder.color} ${folder.filled && isActive ? 'fill-current' : ''}`} />
                  {folder.label[language] || folder.label.en}
                </span>
                {count > 0 && (
                  <Badge variant="secondary" className={`${isActive ? 'bg-blue-200' : 'bg-slate-200'} text-xs`}>
                    {count}
                  </Badge>
                )}
              </button>
            );
          })}
          
          {/* Divider */}
          <div className="border-t my-2" />
          
          {/* Custom Folders Section */}
          <div className="flex items-center justify-between px-3 py-1">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
              {t.customFolders}
            </span>
            <div className="flex items-center gap-0.5">
              <button
                onClick={exportFoldersJson}
                className="p-1 rounded hover:bg-slate-200 transition-colors"
                title={language === 'es' ? 'Exportar carpetas (JSON)' : 'Export folders (JSON)'}
                data-testid="folders-export-json"
              >
                <Download className="h-3.5 w-3.5 text-slate-500" />
              </button>
              <label
                className="p-1 rounded hover:bg-slate-200 transition-colors cursor-pointer"
                title={language === 'es' ? 'Importar carpetas (JSON)' : 'Import folders (JSON)'}
              >
                <Upload className="h-3.5 w-3.5 text-slate-500" />
                <input
                  type="file"
                  accept=".json,application/json"
                  className="hidden"
                  onChange={(e) => { importFoldersJson(e.target.files?.[0]); e.target.value = ''; }}
                  data-testid="folders-import-json-input"
                />
              </label>
              <button
                onClick={() => setShowCreateFolder(!showCreateFolder)}
                className="p-1 rounded hover:bg-slate-200 transition-colors"
                title={t.newFolder}
              >
                <Plus className="h-4 w-4 text-slate-500" />
              </button>
            </div>
          </div>
          
          {/* Create Folder Form */}
          {showCreateFolder && (
            <div className="mx-2 p-2 bg-white border rounded-lg shadow-sm space-y-2">
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder={t.folderName}
                className="h-8 text-sm"
              />
              <div className="flex items-center gap-2">
                <Label className="text-xs">{t.folderColor}:</Label>
                <input
                  type="color"
                  value={newFolderColor}
                  onChange={(e) => setNewFolderColor(e.target.value)}
                  className="w-6 h-6 rounded cursor-pointer border"
                />
              </div>
              <div className="flex gap-1">
                <Button size="sm" className="h-7 text-xs flex-1" onClick={createFolder}>
                  <Check className="h-3 w-3 mr-1" />
                  {t.createFolder}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowCreateFolder(false)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Custom Folders List */}
          {customFolders.map((folder) => {
            const isActive = currentFolder === `custom_${folder.id}` && !showTemplates;
            const count = folderCounts[`custom_${folder.id}`] || 0;
            
            return (
              <div key={folder.id} className="group relative flex items-center">
                <button
                  onClick={() => {
                    setCurrentFolder(`custom_${folder.id}`);
                    setEmailPage(1);
                    setSelectedConversation(null);
                    setShowTemplates(false);
                  }}
                  className={`flex-1 flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <Folder className="h-4 w-4" style={{ color: folder.color }} />
                    <span className="truncate">{folder.name}</span>
                  </span>
                  {count > 0 && (
                    <Badge variant="secondary" className={`${isActive ? 'bg-blue-200' : 'bg-slate-200'} text-xs`}>
                      {count}
                    </Badge>
                  )}
                </button>
                {/* Folder options menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-slate-200 transition-all"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="h-4 w-4 text-slate-500" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    <DropdownMenuItem 
                      onClick={() => {
                        const newName = prompt(language === 'es' ? 'Nuevo nombre:' : 'New name:', folder.name);
                        if (newName && newName.trim() && newName !== folder.name) {
                          // Rename folder
                          axios.put(`${API}/email-tool/custom-folders/${folder.id}`, { name: newName.trim() })
                            .then(() => {
                              toast.success(language === 'es' ? 'Carpeta renombrada' : 'Folder renamed');
                              loadCustomFolders();
                            })
                            .catch(() => toast.error(language === 'es' ? 'Error' : 'Error'));
                        }
                      }}
                    >
                      <Edit2 className="h-4 w-4 mr-2" />
                      {language === 'es' ? 'Modificar etiqueta' : 'Edit label'}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => deleteCustomFolder(folder.id)}
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {language === 'es' ? 'Eliminar etiqueta' : 'Delete label'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })}
          
          {/* Divider before templates */}
          <div className="border-t my-2" />
          
          {/* Templates Button */}
          <button
            onClick={() => {
              setShowTemplates(true);
              setSelectedConversation(null);
            }}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              showTemplates 
                ? 'bg-purple-100 text-purple-700' 
                : 'text-slate-700 hover:bg-slate-100'
            }`}
          >
            <span className="flex items-center gap-3">
              <FileText className={`h-4 w-4 ${showTemplates ? 'text-purple-600' : 'text-slate-500'}`} />
              {t.templates}
            </span>
            {templates.length > 0 && (
              <Badge variant="secondary" className={`${showTemplates ? 'bg-purple-200' : 'bg-slate-200'} text-xs`}>
                {templates.length}
              </Badge>
            )}
          </button>
        </nav>
      </div>

      {/* ========== MAIN CONTENT ========== */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header Bar */}
        <div className="flex items-center gap-4 px-4 py-3 border-b bg-white">
          {selectedConversation ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedConversation(null)}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                {t.back}
              </Button>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold truncate flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${getAccountColor(activeAccount).bg} ${getAccountColor(activeAccount).text}`}
                    data-testid={`active-account-badge-${activeAccount}`}
                    title={accounts.find(a => a.id === activeAccount)?.email || activeAccount}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${getAccountColor(activeAccount).dot}`} />
                    {getAccountColor(activeAccount).label}
                  </span>
                  <span className="truncate">{selectedConversation.subject}</span>
                </h2>
              </div>
              {/* Summarize button */}
              <Button
                variant="outline"
                size="sm"
                onClick={summarizeThread}
                disabled={summarizing}
                className="text-purple-600 border-purple-200 hover:bg-purple-50"
              >
                {summarizing ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <FileText className="h-4 w-4 mr-1" />
                )}
                Resumir
              </Button>
              {/* Translate thread button - toggles between translated and original */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (Object.keys(translatedMessages).length > 0) {
                    setTranslatedMessages({});
                  } else {
                    translateThread();
                  }
                }}
                disabled={translatingThread}
                className={Object.keys(translatedMessages).length > 0 
                  ? "text-green-600 border-green-200 hover:bg-green-50" 
                  : "text-blue-600 border-blue-200 hover:bg-blue-50"}
              >
                {translatingThread ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Globe className="h-4 w-4 mr-1" />
                )}
                {Object.keys(translatedMessages).length > 0 
                  ? (language === 'es' ? 'Ver original' : 'View original')
                  : (language === 'es' ? 'Traducir' : 'Translate')}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={markConversationUnread}>
                    <MailOpen className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'Marcar como no leído' : 'Mark as unread'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => moveToFolder(selectedConversation.thread_id, 'archived')}>
                    <Archive className="h-4 w-4 mr-2" />
                    {t.archive}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => moveToFolder(selectedConversation.thread_id, 'important')}>
                    <Star className="h-4 w-4 mr-2" />
                    {t.markImportant}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => moveToFolder(selectedConversation.thread_id, 'spam')}>
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    {t.markSpam}
                  </DropdownMenuItem>
                  
                  {/* Custom folders */}
                  {customFolders.length > 0 && (
                    <>
                      <div className="border-t my-1" />
                      <div className="px-2 py-1 text-xs text-slate-500">{t.moveTo}:</div>
                      {customFolders.map((folder) => (
                        <DropdownMenuItem 
                          key={folder.id}
                          onClick={() => moveToFolder(selectedConversation.thread_id, `custom_${folder.id}`)}
                        >
                          <Folder className="h-4 w-4 mr-2" style={{ color: folder.color }} />
                          {folder.name}
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                  
                  <div className="border-t my-1" />
                  <DropdownMenuItem 
                    onClick={() => deleteConversation(selectedConversation.thread_id, selectedConversation.folder === 'trash')}
                    className="text-red-600"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {selectedConversation.folder === 'trash' 
                      ? (language === 'es' ? 'Eliminar permanentemente' : 'Delete permanently')
                      : t.delete
                    }
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setEmailPage(1); }}
                  placeholder={t.search}
                  className="pl-10 bg-slate-100 border-slate-200 focus:bg-white h-10 text-base"
                  data-testid="email-search-input"
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => setSearchQuery('')}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              {/* Empty Trash button - only shown when in trash folder */}
              {currentFolder === 'trash' && filteredConversations.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    if (window.confirm(language === 'es' 
                      ? '¿Vaciar papelera? Esta acción no se puede deshacer.'
                      : 'Empty trash? This action cannot be undone.'
                    )) {
                      try {
                        await axios.delete(`${API}/email-tool/conversations/trash/empty`);
                        toast.success(language === 'es' ? 'Papelera vaciada' : 'Trash emptied');
                        loadConversations();
                        loadFolderCounts();
                      } catch (error) {
                        toast.error(language === 'es' ? 'Error al vaciar' : 'Error emptying');
                      }
                    }
                  }}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  {language === 'es' ? 'Vaciar' : 'Empty'}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={syncEmails}
                disabled={conversationsLoading}
                title={language === 'es' ? 'Sincronizar emails' : 'Sync emails'}
              >
                <RefreshCw className={`h-4 w-4 ${conversationsLoading ? 'animate-spin' : ''}`} />
              </Button>
            </>
          )}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden">
          {showTemplates ? (
            /* ========== TEMPLATES VIEW ========== */
            <div className="h-full flex flex-col">
              {/* Templates Header */}
              <div className="p-4 border-b bg-slate-50 flex items-center justify-between gap-2 flex-wrap">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="h-5 w-5 text-purple-600" />
                  {t.templates}
                </h2>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={exportTemplatesJson}
                    className="h-8"
                    data-testid="templates-export-json"
                  >
                    <Download className="h-3.5 w-3.5 mr-1" />
                    {language === 'es' ? 'Exportar JSON' : 'Export JSON'}
                  </Button>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".json,application/json"
                      className="hidden"
                      onChange={(e) => { importTemplatesJson(e.target.files?.[0]); e.target.value = ''; }}
                      data-testid="templates-import-json-input"
                    />
                    <span className="inline-flex items-center h-8 px-3 text-xs font-medium rounded-md border bg-background hover:bg-accent">
                      <Upload className="h-3.5 w-3.5 mr-1" />
                      {language === 'es' ? 'Importar JSON' : 'Import JSON'}
                    </span>
                  </label>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4">
                {/* New/Edit Template Form */}
                <Card className="mb-6 border-purple-200">
                  <CardContent className="p-4 space-y-4">
                    <h3 className="font-medium flex items-center gap-2">
                      {editingTemplate ? (
                        <>
                          <Edit3 className="h-4 w-4" />
                          {t.editTemplate}
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4" />
                          {t.newTemplate}
                        </>
                      )}
                    </h3>
                    
                    <div className="space-y-3">
                      <div>
                        <Label>{t.templateName}</Label>
                        <Input
                          value={newTemplate.name}
                          onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                          placeholder={language === 'es' ? 'Nombre de la plantilla...' : 'Template name...'}
                        />
                      </div>
                      <div>
                        <Label>{t.subject}</Label>
                        <Input
                          value={newTemplate.subject}
                          onChange={(e) => setNewTemplate(prev => ({ ...prev, subject: e.target.value }))}
                          placeholder={language === 'es' ? 'Asunto del email...' : 'Email subject...'}
                        />
                      </div>
                      <div>
                        <Label>{t.message}</Label>
                        <RichTextEditor
                          value={newTemplate.body}
                          onChange={(html) => setNewTemplate(prev => ({ ...prev, body: html }))}
                          placeholder={language === 'es' ? 'Contenido del email...' : 'Email content...'}
                          minHeight="180px"
                        />
                      </div>
                      
                      <div className="flex gap-2">
                        <Button onClick={saveTemplate} className="bg-purple-600 hover:bg-purple-700">
                          <Check className="h-4 w-4 mr-2" />
                          {t.saveTemplate}
                        </Button>
                        {editingTemplate && (
                          <Button 
                            variant="outline" 
                            onClick={() => {
                              setEditingTemplate(null);
                              setNewTemplate({ name: '', subject: '', body: '' });
                            }}
                          >
                            {t.cancel}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Templates List */}
                {templates.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t.noTemplates}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {templates.map((template) => (
                      <Card key={template.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold">{template.name}</h4>
                              <p className="text-sm text-muted-foreground truncate">{template.subject}</p>
                              <p className="text-sm text-slate-500 mt-1 line-clamp-3">{template.body_text || template.body}</p>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => applyTemplate(template)}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setEditingTemplate(template);
                                  setNewTemplate({
                                    name: template.name,
                                    subject: template.subject,
                                    body: template.body_text || template.body,
                                  });
                                }}
                              >
                                <Edit3 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => deleteTemplate(template.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : selectedConversation ? (
            /* ========== CONVERSATION DETAIL VIEW ========== */
            <div className="h-full flex flex-col">
              {/* Thread Summary */}
              {threadSummary && (
                <div className="mx-4 mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-purple-700 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Resumen del hilo
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setThreadSummary('')}
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="text-sm text-purple-900 whitespace-pre-wrap">
                    {threadSummary}
                  </div>
                </div>
              )}
              
              {/* Thread Translation */}
              {threadTranslation && (
                <div className="mx-4 mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-blue-700 flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      Traducción a español
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setThreadTranslation('')}
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="text-sm text-blue-900 whitespace-pre-wrap">
                    {threadTranslation}
                  </div>
                </div>
              )}
              
              {/* Messages List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {selectedConversation.messages.map((msg, idx) => {
                  const isUnread = msg.type === 'received' && msg.is_read === false;
                  const isSent = msg.type === 'sent';
                  
                  return (
                    <div
                      key={msg.id || idx}
                      className={`rounded-lg border p-4 transition-all ${
                        isSent 
                          ? 'bg-blue-50/50 border-blue-200 ml-8' 
                          : isUnread
                            ? 'bg-amber-50 border-amber-300 border-l-4 border-l-amber-500'
                            : 'bg-white border-slate-200 mr-8'
                      }`}
                    >
                      {/* Message Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {/* Avatar */}
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold ${
                            isSent ? 'bg-blue-500' : isUnread ? 'bg-amber-500' : 'bg-slate-400'
                          }`}>
                            {isSent 
                              ? 'T' 
                              : (msg.from_name?.charAt(0)?.toUpperCase() || '?')}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`font-semibold ${isUnread ? 'text-amber-800' : ''}`}>
                                {isSent ? t.you : msg.from_name}
                              </span>
                              {isUnread && (
                                <Badge className="bg-amber-100 text-amber-700 text-xs">
                                  {language === 'es' ? 'Nuevo' : 'New'}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {isSent ? 'contact@purolink.store' : msg.from_email}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(msg.date).toLocaleString('es-ES', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>

                      {/* Message Body - Render HTML with scroll for long emails */}
                      <div 
                        className={`email-content text-base leading-relaxed ${isUnread ? 'font-medium' : ''}`}
                        style={{
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          maxHeight: '400px',
                          overflowY: 'auto'
                        }}
                        dangerouslySetInnerHTML={{ 
                          __html: (msg.body_html 
                            ? cleanQuotedContent(msg.body_html) 
                            : cleanQuotedContent(msg.body)?.replace(/\n/g, '<br/>') || ''
                          ).replace(/<a\s/g, '<a target="_blank" rel="noopener noreferrer" ')
                        }}
                      />
                      <style>{`
                        .email-content img { max-width: 100%; height: auto; }
                        .email-content table { max-width: 100%; }
                        .email-content a { color: #7c3aed; text-decoration: underline; }
                        .email-content p { margin: 0.5em 0; }
                        .email-content br { line-height: 1.5; }
                        .email-content blockquote { 
                          border-left: 3px solid #e2e8f0; 
                          padding-left: 1em; 
                          margin: 0.5em 0;
                          color: #64748b;
                        }
                      `}</style>

                      {/* Inline translation from "Traducir" button */}
                      {translatedMessages[msg.id] && (
                        <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <span className="text-xs font-medium text-blue-600 flex items-center gap-1 mb-1">
                            <Globe className="h-3 w-3" />
                            Traducción
                          </span>
                          <p className="text-sm text-blue-900 whitespace-pre-wrap">{translatedMessages[msg.id]}</p>
                        </div>
                      )}

                      {/* Attachments */}
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="mt-3 pt-3 border-t flex flex-wrap gap-2">
                          {msg.attachments.map((att, attIdx) => {
                            // For received emails, attachments can be downloaded from IMAP
                            // For sent emails, only the filename is stored (no download available)
                            const isReceived = msg.type === 'received';
                            const filename = att.filename || att;
                            
                            if (isReceived) {
                              return (
                                <a
                                  key={attIdx}
                                  href={`${API}/email-tool/inbox/${msg.id}/attachment/${attIdx}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  download={filename}
                                  className="inline-flex items-center px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 hover:bg-blue-100 transition-colors cursor-pointer"
                                >
                                  <Paperclip className="h-3 w-3 mr-1" />
                                  {filename}
                                </a>
                              );
                            }
                            
                            // For sent emails, just show the filename (no download)
                            return (
                              <span
                                key={attIdx}
                                className="inline-flex items-center px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600"
                                title={language === 'es' ? 'Adjunto enviado (no descargable)' : 'Sent attachment (not downloadable)'}
                              >
                                <Paperclip className="h-3 w-3 mr-1" />
                                {filename}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Translate button for received messages */}
                      {msg.type === 'received' && (
                        <div className="mt-3 pt-3 border-t">
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Reply Section */}
              <div className="border-t bg-slate-50 p-4 space-y-3">
                {/* Quick Replies */}
                {(quickReplies.length > 0 || loadingQuickReplies) && (
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Sparkles className="h-3 w-3" />
                      {t.quickReplies}:
                    </span>
                    {loadingQuickReplies ? (
                      <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
                    ) : (
                      quickReplies.map((reply, idx) => (
                        <button
                          key={idx}
                          onClick={() => setReplyText(reply.text)}
                          className="px-3 py-1.5 bg-white border border-emerald-200 rounded-full text-xs hover:bg-emerald-50 transition-colors"
                          title={reply.text}
                        >
                          <span className="mr-1">{reply.emoji}</span>
                          {reply.label}
                        </button>
                      ))
                    )}
                  </div>
                )}

                {/* AI Draft */}
                {aiDraft && (
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-purple-700 flex items-center gap-1">
                        <Sparkles className="h-3 w-3" />
                        Borrador IA
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setReplyText(aiDraft); setAiDraft(''); }}
                        className="text-purple-700 text-xs h-7"
                      >
                        <Check className="h-3 w-3 mr-1" />
                        {t.useDraft}
                      </Button>
                    </div>
                    <p className="text-base text-purple-800 whitespace-pre-wrap">{aiDraft}</p>
                  </div>
                )}

                {/* Reply Input */}
                <div className="space-y-3">
                  {/* Attachment button and list for reply */}
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      ref={replyFileInputRef}
                      onChange={(e) => handleFileSelect(e, true)}
                      multiple
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => replyFileInputRef.current?.click()}
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    >
                      <Paperclip className="h-4 w-4 mr-1" />
                      {t.attachFiles}
                    </Button>
                    {replyAttachments.length > 0 && (
                      <span className="text-xs text-slate-500">
                        {replyAttachments.length} {language === 'es' ? 'archivo(s)' : 'file(s)'}
                      </span>
                    )}
                  </div>
                  
                  {/* Reply Attached Files */}
                  {replyAttachments.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {replyAttachments.map((file, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 px-2 py-1 bg-blue-50 rounded border border-blue-200 text-xs"
                        >
                          <Paperclip className="h-3 w-3 text-blue-500" />
                          <span className="truncate max-w-[100px]">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => removeAttachment(idx, true)}
                            className="text-red-400 hover:text-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Translation helper - shows if different language detected */}
                  {/* Translation bar - always show with language options */}
                  <div className="mb-3 p-2 bg-slate-50 border border-slate-200 rounded-lg">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Globe className="h-4 w-4 flex-shrink-0" />
                        <span>
                          {detectedLanguage !== 'es' 
                            ? <>Idioma: <strong>{getLanguageName(detectedLanguage)}</strong></>
                            : 'Traducir respuesta'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        {detectedLanguage !== 'es' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => translateReplyToLanguage(detectedLanguage)}
                            disabled={translatingReply || !replyText.trim() || replyText === '<p></p>'}
                            className="text-amber-700 border-amber-300 hover:bg-amber-100 whitespace-nowrap h-7 text-xs"
                          >
                            {translatingReply ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                            ) : (
                              <Languages className="h-3.5 w-3.5 mr-1" />
                            )}
                            {getLanguageName(detectedLanguage)}
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={translatingReply || !replyText.trim() || replyText === '<p></p>'}
                              className="text-slate-600 border-slate-300 hover:bg-slate-100 h-7 text-xs"
                            >
                              {detectedLanguage === 'es' && <Languages className="h-3.5 w-3.5 mr-1" />}
                              {detectedLanguage === 'es' ? 'Idioma' : 'Otro'}
                              <ChevronRight className="h-3.5 w-3.5 ml-1 rotate-90" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {[
                              {code: 'en', name: 'Inglés'},
                              {code: 'fr', name: 'Francés'},
                              {code: 'de', name: 'Alemán'},
                              {code: 'it', name: 'Italiano'},
                              {code: 'pt', name: 'Portugués'},
                              {code: 'nl', name: 'Holandés'},
                              {code: 'pl', name: 'Polaco'},
                              {code: 'es', name: 'Español'},
                            ].filter(l => l.code !== detectedLanguage).map(lang => (
                              <DropdownMenuItem
                                key={lang.code}
                                onClick={() => translateReplyToLanguage(lang.code)}
                              >
                                <Languages className="h-3.5 w-3.5 mr-2" />
                                {lang.name}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <RichTextEditor
                      value={replyText}
                      onChange={(html) => setReplyText(html)}
                      placeholder={t.writeReply}
                      minHeight="120px"
                    />
                    <div className="flex items-center justify-between">
                      <div className="flex gap-1">
                        {/* Template selector for replies */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs text-blue-600"
                              title={language === 'es' ? 'Usar plantilla' : 'Use template'}
                            >
                              <FileText className="h-3.5 w-3.5 mr-1" />
                              {language === 'es' ? 'Plantilla' : 'Template'}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-56">
                            <div className="px-2 py-1.5 text-xs font-medium text-slate-500">
                              {language === 'es' ? 'Plantillas' : 'Templates'}
                            </div>
                            {templates.length === 0 ? (
                              <div className="px-2 py-3 text-sm text-slate-400 text-center">
                                {t.noTemplates}
                              </div>
                            ) : (
                              templates.map((tmpl) => (
                                <DropdownMenuItem
                                  key={tmpl.id}
                                  onClick={() => applyTemplateAndTranslate(tmpl.body_text || tmpl.body || '')}
                                >
                                  <FileText className="h-3.5 w-3.5 mr-2 text-blue-500" />
                                  <span className="truncate">{tmpl.name}</span>
                                  {detectedLanguage && detectedLanguage !== 'es' && (
                                    <Globe className="h-3 w-3 ml-auto text-amber-500" />
                                  )}
                                </DropdownMenuItem>
                              ))
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        {/* Save current reply as template */}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs text-green-600"
                          title={language === 'es' ? 'Crear plantilla con este texto' : 'Create template from this text'}
                          onClick={() => {
                            const name = window.prompt(language === 'es' ? 'Nombre de la plantilla:' : 'Template name:');
                            if (name && replyText.trim()) {
                              axios.post(`${API}/email-tool/templates`, {
                                name,
                                subject: selectedConversation?.subject || '',
                                body_text: replyText,
                                description: ''
                              }).then(() => {
                                toast.success(t.templateSaved);
                                loadTemplates();
                              }).catch(() => {
                                toast.error(language === 'es' ? 'Error guardando' : 'Error saving');
                              });
                            }
                          }}
                          disabled={!replyText.trim() || replyText === '<p></p>'}
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" />
                          {language === 'es' ? 'Nueva plantilla' : 'New template'}
                        </Button>
                        {/* AI Draft */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={generateAIDraft}
                          disabled={generatingDraft}
                          className="h-7 text-xs text-purple-600"
                          title="Generar borrador con IA"
                        >
                          {generatingDraft ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                          ) : (
                            <Sparkles className="h-3.5 w-3.5 mr-1" />
                          )}
                          IA
                        </Button>
                      </div>
                      <Button
                        onClick={sendReply}
                        disabled={sendingReply || !replyText.trim() || replyText === '<p></p>'}
                        size="sm"
                      >
                        {sendingReply ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                          <Send className="h-4 w-4 mr-1" />
                        )}
                        {language === 'es' ? 'Enviar' : 'Send'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* ========== CONVERSATIONS LIST ========== */
            <div className="h-full overflow-y-auto">
              {conversationsLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <Mail className="h-12 w-12 mb-4 opacity-50" />
                  <p>{t.noEmails}</p>
                </div>
              ) : (
                <div className="flex flex-col h-full">
                {/* === Bulk selection toolbar === */}
                {(() => {
                  const visibleIds = filteredConversations
                    .slice((emailPage - 1) * 10, emailPage * 10)
                    .map(c => c.thread_id);
                  const allChecked = isAllVisibleSelected(visibleIds);
                  const someChecked = visibleIds.some(id => selectedThreadIds.has(id));
                  const selectedCount = selectedThreadIds.size;
                  return (
                    <div
                      className="flex items-center gap-2 px-4 py-2 border-b bg-slate-50/80 sticky top-0 z-10"
                      data-testid="email-bulk-toolbar"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 cursor-pointer accent-rose-600"
                        checked={allChecked}
                        ref={(el) => { if (el) el.indeterminate = !allChecked && someChecked; }}
                        onChange={(e) => {
                          if (e.target.checked) selectAllVisible(visibleIds);
                          else clearSelection();
                        }}
                        title={language === 'es' ? 'Seleccionar todo (página)' : 'Select all (page)'}
                        data-testid="bulk-select-all-checkbox"
                        aria-label={language === 'es' ? 'Seleccionar todo' : 'Select all'}
                      />
                      <span className="text-xs text-slate-600 select-none">
                        {selectedCount === 0
                          ? (language === 'es' ? 'Seleccionar' : 'Select')
                          : (language === 'es' ? `${selectedCount} seleccionada${selectedCount === 1 ? '' : 's'}` : `${selectedCount} selected`)
                        }
                      </span>

                      {selectedCount > 0 && (
                        <>
                          <span className="text-slate-300 mx-1">·</span>
                          {/* Move to folder (bulk) */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs"
                                disabled={bulkBusy}
                                data-testid="bulk-move-trigger"
                              >
                                <FolderInput className="h-3.5 w-3.5 mr-1" />
                                {language === 'es' ? 'Mover a' : 'Move to'}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-44">
                              <DropdownMenuItem onClick={() => bulkMoveToFolder('inbox')}>
                                <Inbox className="h-4 w-4 mr-2" />
                                {language === 'es' ? 'Recibidos' : 'Inbox'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => bulkMoveToFolder('archived')}>
                                <Archive className="h-4 w-4 mr-2" />
                                {language === 'es' ? 'Archivados' : 'Archived'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => bulkMoveToFolder('important')}>
                                <AlertCircle className="h-4 w-4 mr-2" />
                                {language === 'es' ? 'Importantes' : 'Important'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => bulkMoveToFolder('spam')}>
                                <AlertTriangle className="h-4 w-4 mr-2" />
                                Spam
                              </DropdownMenuItem>
                              {customFolders.length > 0 && (
                                <>
                                  <DropdownMenuSeparator />
                                  {customFolders.map(folder => (
                                    <DropdownMenuItem
                                      key={folder.id}
                                      onClick={() => bulkMoveToFolder(`custom_${folder.id}`)}
                                    >
                                      <Tag className="h-4 w-4 mr-2" style={{ color: folder.color }} />
                                      {folder.name}
                                    </DropdownMenuItem>
                                  ))}
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>

                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            onClick={bulkDelete}
                            disabled={bulkBusy}
                            data-testid="bulk-delete-button"
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            {currentFolder === 'trash'
                              ? (language === 'es' ? 'Eliminar def.' : 'Delete')
                              : (language === 'es' ? 'A papelera' : 'To trash')
                            }
                          </Button>

                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs ml-auto"
                            onClick={clearSelection}
                            disabled={bulkBusy}
                            data-testid="bulk-clear-selection"
                          >
                            <X className="h-3.5 w-3.5 mr-1" />
                            {language === 'es' ? 'Limpiar' : 'Clear'}
                          </Button>
                        </>
                      )}
                    </div>
                  );
                })()}
                <div className="divide-y flex-1 overflow-y-auto">
                  {filteredConversations.slice((emailPage - 1) * 10, emailPage * 10).map((conv) => {
                    const hasUnread = conv.unread_count > 0;
                    const lastMessage = conv.messages?.[conv.messages.length - 1];
                    const isSelected = selectedThreadIds.has(conv.thread_id);
                    
                    return (
                      <div
                        key={conv.thread_id}
                        onClick={() => openConversation(conv)}
                        className={`group flex items-center gap-4 px-4 py-3 cursor-pointer transition-colors hover:bg-slate-50 border-l-4 ${getAccountColor(activeAccount).border} ${
                          isSelected ? 'bg-rose-50/60' : (hasUnread ? 'bg-blue-50/50' : '')
                        }`}
                        data-testid={`conversation-item-${activeAccount}`}
                      >
                        {/* Bulk-select checkbox */}
                        <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 cursor-pointer accent-rose-600"
                            checked={isSelected}
                            onChange={() => toggleSelectThread(conv.thread_id)}
                            aria-label={language === 'es' ? 'Seleccionar conversación' : 'Select conversation'}
                            data-testid="bulk-select-row-checkbox"
                          />
                        </div>
                        {/* Star & Delete & Archive Buttons */}
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          {/* Star with color menu */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className={`transition-colors p-1 ${
                                  conv.star_color 
                                    ? getStarColor(conv.star_color).color 
                                    : 'text-slate-300 hover:text-amber-400'
                                }`}
                                title={conv.star_color ? t.unstar : t.starred}
                              >
                                <Star className={`h-4 w-4 ${
                                  conv.star_color 
                                    ? `${getStarColor(conv.star_color).fill} ${getStarColor(conv.star_color).color}` 
                                    : ''
                                }`} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-36">
                              {conv.star_color && (
                                <>
                                  <DropdownMenuItem onClick={() => toggleStarred(conv.thread_id, true)}>
                                    <X className="h-4 w-4 mr-2 text-slate-400" />
                                    {language === 'es' ? 'Quitar estrella' : 'Remove star'}
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                </>
                              )}
                              {starColors.map((sc) => (
                                <DropdownMenuItem 
                                  key={sc.id} 
                                  onClick={() => toggleStarred(conv.thread_id, false, sc.id)}
                                  className="flex items-center"
                                >
                                  <Star className={`h-4 w-4 mr-2 ${sc.fill} ${sc.color}`} />
                                  {sc.label[language] || sc.label.en}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          {/* Move to folder menu */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="text-slate-300 hover:text-blue-500 transition-colors p-1 opacity-0 group-hover:opacity-100"
                                title={language === 'es' ? 'Mover a carpeta' : 'Move to folder'}
                              >
                                <FolderInput className="h-4 w-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-44">
                              <DropdownMenuItem onClick={() => moveToFolder(conv.thread_id, 'inbox')}>
                                <Inbox className="h-4 w-4 mr-2" />
                                {language === 'es' ? 'Recibidos' : 'Inbox'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => moveToFolder(conv.thread_id, 'archived')}>
                                <Archive className="h-4 w-4 mr-2" />
                                {language === 'es' ? 'Archivados' : 'Archived'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => moveToFolder(conv.thread_id, 'important')}>
                                <AlertCircle className="h-4 w-4 mr-2" />
                                {language === 'es' ? 'Importantes' : 'Important'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => moveToFolder(conv.thread_id, 'spam')}>
                                <AlertTriangle className="h-4 w-4 mr-2" />
                                Spam
                              </DropdownMenuItem>
                              {customFolders.length > 0 && (
                                <>
                                  <DropdownMenuSeparator />
                                  {customFolders.map(folder => (
                                    <DropdownMenuItem 
                                      key={folder.id}
                                      onClick={() => moveToFolder(conv.thread_id, `custom_${folder.id}`)}
                                    >
                                      <Tag className="h-4 w-4 mr-2" style={{color: folder.color}} />
                                      {folder.name}
                                    </DropdownMenuItem>
                                  ))}
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteConversation(conv.thread_id, currentFolder === 'trash');
                            }}
                            className="text-slate-300 hover:text-red-500 transition-colors p-1 opacity-0 group-hover:opacity-100"
                            title={currentFolder === 'trash' 
                              ? (language === 'es' ? 'Eliminar permanentemente' : 'Delete permanently')
                              : t.delete
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Avatar */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 ${
                          hasUnread ? 'bg-blue-500' : 'bg-slate-400'
                        }`}>
                          {conv.contact_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className={`truncate ${hasUnread ? 'font-bold' : 'font-medium'}`}>
                              {conv.contact_name || conv.contact_email}
                            </span>
                            <span className={`text-xs flex-shrink-0 ml-2 ${hasUnread ? 'text-blue-600 font-semibold' : 'text-muted-foreground'}`}>
                              {formatDate(conv.last_date || conv.last_message_date || lastMessage?.date)}
                            </span>
                          </div>
                          <p className={`text-sm truncate ${hasUnread ? 'font-semibold text-slate-800' : 'text-muted-foreground'}`}>
                            {conv.subject}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {lastMessage?.body?.substring(0, 80)}...
                          </p>
                          {conv.folder && conv.folder !== 'inbox' && conv.folder !== 'sent' && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 mt-0.5 border-blue-200 text-blue-600 bg-blue-50">
                              {conv.folder.startsWith('custom_') 
                                ? customFolders.find(f => `custom_${f.id}` === conv.folder)?.name || conv.folder.replace('custom_', '')
                                : conv.folder === 'archived' ? (language === 'es' ? 'Archivado' : 'Archived')
                                : conv.folder === 'important' ? (language === 'es' ? 'Importante' : 'Important')
                                : conv.folder === 'spam' ? 'Spam'
                                : conv.folder === 'trash' ? (language === 'es' ? 'Papelera' : 'Trash')
                                : conv.folder
                              }
                            </Badge>
                          )}
                        </div>

                        {/* Badges - Only show unread count for received messages */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {conv.unread_count > 0 && (
                            <Badge variant="default" className="text-xs bg-blue-500 text-white">
                              {conv.unread_count}
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Pagination */}
                {filteredConversations.length > 10 && (
                  <div className="flex items-center justify-between px-4 py-2 border-t bg-slate-50 shrink-0">
                    <span className="text-xs text-slate-500">
                      {(emailPage - 1) * 10 + 1}-{Math.min(emailPage * 10, filteredConversations.length)} de {filteredConversations.length}
                    </span>
                    <div className="flex gap-1">
                      <button
                        disabled={emailPage <= 1}
                        onClick={() => setEmailPage(p => Math.max(1, p - 1))}
                        className="px-2 py-1 text-xs rounded border bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {language === 'es' ? 'Anterior' : 'Prev'}
                      </button>
                      <span className="px-2 py-1 text-xs text-slate-600">{emailPage}/{Math.ceil(filteredConversations.length / 10)}</span>
                      <button
                        disabled={emailPage >= Math.ceil(filteredConversations.length / 10)}
                        onClick={() => setEmailPage(p => Math.min(Math.ceil(filteredConversations.length / 10), p + 1))}
                        className="px-2 py-1 text-xs rounded border bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {language === 'es' ? 'Siguiente' : 'Next'}
                      </button>
                    </div>
                  </div>
                )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ========== COMPOSE DIALOG ========== */}
      <Dialog open={showCompose} onOpenChange={setShowCompose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="h-5 w-5" />
              {t.compose}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Template Selector */}
            {templates.length > 0 && (
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4 text-purple-600" />
                  <span className="font-medium text-sm text-purple-700">{t.useTemplate}</span>
                </div>
                <Select
                  onValueChange={(templateId) => {
                    const template = templates.find(t => t.id === templateId);
                    if (template) {
                      setComposeData(prev => ({
                        ...prev,
                        subject: template.subject,
                        body: template.body_text || template.body,
                        translatedBody: '',
                      }));
                    }
                  }}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder={t.selectTemplate} />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* To */}
            <div className="flex gap-2">
              <div className="flex-1">
                <Label>
                  {t.to}
                  <span className="ml-2 text-[10px] font-normal text-slate-500">
                    {language === 'es'
                      ? '(varios separados por ; o ,)'
                      : '(separate multiple with ; or ,)'}
                  </span>
                </Label>
                <Input
                  value={composeData.to}
                  onChange={(e) => setComposeData(prev => ({ ...prev, to: e.target.value }))}
                  placeholder="email1@ejemplo.com; email2@ejemplo.com"
                  data-testid="compose-to-input"
                />
              </div>
              <div className="w-1/3">
                <Label>{language === 'es' ? 'Nombre' : 'Name'}</Label>
                <Input
                  value={composeData.toName}
                  onChange={(e) => setComposeData(prev => ({ ...prev, toName: e.target.value }))}
                  placeholder={language === 'es' ? 'Nombre (opcional)' : 'Name (optional)'}
                />
              </div>
            </div>

            {/* CC & BCC */}
            <div className="flex gap-2">
              <div className="flex-1">
                <Label>CC ({language === 'es' ? 'Con copia' : 'Carbon copy'})</Label>
                <Input
                  value={composeData.cc}
                  onChange={(e) => setComposeData(prev => ({ ...prev, cc: e.target.value }))}
                  placeholder={language === 'es' ? 'email1@ej.com, email2@ej.com' : 'email1@ex.com, email2@ex.com'}
                  data-testid="compose-cc"
                />
              </div>
              <div className="flex-1">
                <Label>CCO ({language === 'es' ? 'Con copia oculta' : 'Blind carbon copy'})</Label>
                <Input
                  value={composeData.bcc}
                  onChange={(e) => setComposeData(prev => ({ ...prev, bcc: e.target.value }))}
                  placeholder={language === 'es' ? 'email1@ej.com, email2@ej.com' : 'email1@ex.com, email2@ex.com'}
                  data-testid="compose-bcc"
                />
              </div>
            </div>

            {/* Subject */}
            <div>
              <Label>{t.subject}</Label>
              <Input
                value={composeData.subject}
                onChange={(e) => setComposeData(prev => ({ ...prev, subject: e.target.value }))}
                placeholder={language === 'es' ? 'Asunto del email' : 'Email subject'}
              />
            </div>

            {/* Message Body */}
            <div>
              <Label>{t.message}</Label>
              <RichTextEditor
                value={composeData.body}
                onChange={(html) => setComposeData(prev => ({ ...prev, body: html, translatedBody: '' }))}
                placeholder={language === 'es' ? 'Escribe tu mensaje...' : 'Write your message...'}
                minHeight="200px"
              />
            </div>

            {/* Attachments Section */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Paperclip className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-sm text-blue-700">{t.attachFiles}</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-green-500" />
                  <span className="text-xs text-green-600">{t.virusScanEnabled}</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleFileSelect(e, false)}
                  multiple
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={scanningFiles}
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  {scanningFiles ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4 mr-1" />
                  )}
                  {scanningFiles ? t.scanningFiles : (language === 'es' ? 'Seleccionar archivos' : 'Select files')}
                </Button>
                <span className="text-xs text-blue-500">{t.maxFileSize}</span>
              </div>
              
              {/* Attached Files List with Scan Status */}
              {attachments.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-blue-600">{t.attachedFiles} ({attachments.length}):</Label>
                  <div className="flex flex-wrap gap-2">
                    {attachments.map((file, idx) => {
                      const scan = attachmentScans[file.name] || {};
                      return (
                        <div
                          key={idx}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm ${
                            scan.scanning 
                              ? 'bg-yellow-50 border-yellow-300' 
                              : scan.safe === false 
                                ? 'bg-red-50 border-red-300'
                                : 'bg-green-50 border-green-300'
                          }`}
                        >
                          {/* Scan Status Icon */}
                          {scan.scanning ? (
                            <Loader2 className="h-3 w-3 text-yellow-500 animate-spin" />
                          ) : scan.safe === false ? (
                            <ShieldAlert className="h-3 w-3 text-red-500" />
                          ) : scan.safe === true ? (
                            <ShieldCheck className="h-3 w-3 text-green-500" />
                          ) : (
                            <Shield className="h-3 w-3 text-slate-400" />
                          )}
                          
                          <Paperclip className="h-3 w-3 text-slate-500" />
                          <span className="truncate max-w-[120px]">{file.name}</span>
                          <span className="text-xs text-slate-400">({formatFileSize(file.size)})</span>
                          <button
                            type="button"
                            onClick={() => removeAttachment(idx, false)}
                            className="text-red-400 hover:text-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Translation Section */}
            <div className="p-4 bg-slate-50 rounded-lg border space-y-3">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-slate-500" />
                <span className="font-medium text-sm">{t.translateTo}</span>
              </div>
              
              <div className="flex gap-2">
                <Select
                  value={composeData.translateTo}
                  onValueChange={(val) => setComposeData(prev => ({ ...prev, translateTo: val, translatedBody: '' }))}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder={language === 'es' ? 'Seleccionar idioma' : 'Select language'} />
                  </SelectTrigger>
                  <SelectContent>
                    {TRANSLATION_LANGUAGES.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>
                        {lang.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button
                  variant="outline"
                  onClick={translateText}
                  disabled={translating || !composeData.body.trim() || !composeData.translateTo}
                >
                  {translating ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Languages className="h-4 w-4 mr-2" />
                  )}
                  {t.translate}
                </Button>
              </div>

              {/* Translated Text Preview */}
              {composeData.translatedBody && (
                <div className="mt-3 p-4 bg-white rounded border">
                  <Label className="text-xs text-muted-foreground">{t.translatedText}</Label>
                  <p className="text-base mt-2 whitespace-pre-wrap">{composeData.translatedBody}</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-3 sticky bottom-0 bg-white border-t -mx-6 px-6 pb-2 z-10">
              <Button
                variant="outline"
                onClick={() => { setShowCompose(false); resetCompose(); }}
              >
                {t.cancel}
              </Button>
              
              {composeData.translatedBody ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => sendEmail(false)}
                    disabled={sending || sendCountdown > 0}
                  >
                    {t.sendOriginal}
                  </Button>
                  <Button
                    onClick={() => sendEmail(true)}
                    disabled={sending || sendCountdown > 0}
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    {t.useTranslation}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => sendEmail(false)}
                  disabled={sending || sendCountdown > 0 || !composeData.to || !composeData.subject || !composeData.body}
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  {t.send}
                </Button>
              )}
              {sendCountdown > 0 && (
                <div className="flex items-center gap-2 ml-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                  <span className="text-sm text-amber-800 font-medium">
                    {language === 'es' ? `Enviando en ${sendCountdown}s...` : `Sending in ${sendCountdown}s...`}
                  </span>
                  <Button
                    onClick={cancelSend}
                    variant="destructive"
                    size="sm"
                    className="h-7 px-3 text-xs"
                  >
                    {language === 'es' ? 'Cancelar' : 'Cancel'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ========== SIGNATURE EDITOR DIALOG ========== */}
      <Dialog open={showSignatureEditor} onOpenChange={setShowSignatureEditor}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              {language === 'es' ? 'Editar Firma de Email' : 'Edit Email Signature'}
            </DialogTitle>
          </DialogHeader>

          {signatureData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">{language === 'es' ? 'Nombre' : 'Name'}</label>
                  <Input value={signatureData.name || ''} onChange={(e) => setSignatureData({...signatureData, name: e.target.value})} data-testid="sig-name" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{language === 'es' ? 'Departamento' : 'Department'}</label>
                  <Input value={signatureData.department || ''} onChange={(e) => setSignatureData({...signatureData, department: e.target.value})} data-testid="sig-department" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{language === 'es' ? 'Teléfono' : 'Phone'}</label>
                  <Input value={signatureData.phone || ''} onChange={(e) => setSignatureData({...signatureData, phone: e.target.value})} data-testid="sig-phone" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Email</label>
                  <Input value={signatureData.email || ''} onChange={(e) => setSignatureData({...signatureData, email: e.target.value})} data-testid="sig-email" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{language === 'es' ? 'Dominio' : 'Website'}</label>
                  <Input value={signatureData.web || ''} onChange={(e) => setSignatureData({...signatureData, web: e.target.value})} data-testid="sig-web" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{language === 'es' ? 'URL del dominio' : 'Website URL'}</label>
                  <Input value={signatureData.web_url || ''} onChange={(e) => setSignatureData({...signatureData, web_url: e.target.value})} data-testid="sig-web-url" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{language === 'es' ? 'Dirección' : 'Address'}</label>
                  <Input value={signatureData.address || ''} onChange={(e) => setSignatureData({...signatureData, address: e.target.value})} data-testid="sig-address" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{language === 'es' ? 'Ciudad' : 'City'}</label>
                  <Input value={signatureData.city || ''} onChange={(e) => setSignatureData({...signatureData, city: e.target.value})} data-testid="sig-city" />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">{language === 'es' ? 'URL de la foto' : 'Photo URL'}</label>
                <div className="flex gap-2 items-center">
                  <Input value={signatureData.image_url || ''} onChange={(e) => setSignatureData({...signatureData, image_url: e.target.value})} className="flex-1" data-testid="sig-image" />
                  {signatureData.image_url && (
                    <img src={signatureData.image_url} alt="Firma" className="h-12 w-12 rounded object-cover border" />
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">{language === 'es' ? 'Color principal' : 'Accent color'}</label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={signatureData.accent_color || '#e91e63'} onChange={(e) => setSignatureData({...signatureData, accent_color: e.target.value})} className="h-9 w-14 rounded cursor-pointer" data-testid="sig-color" />
                  <span className="text-sm text-muted-foreground">{signatureData.accent_color}</span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">{language === 'es' ? 'Texto legal (RGPD)' : 'Legal text (GDPR)'}</label>
                <textarea value={signatureData.legal_text || ''} onChange={(e) => setSignatureData({...signatureData, legal_text: e.target.value})} className="w-full h-24 p-2 text-xs border rounded resize-y" data-testid="sig-legal" />
              </div>

              {/* Preview */}
              {signaturePreview && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">{language === 'es' ? 'Vista previa' : 'Preview'}</label>
                  <div className="border rounded-lg p-4 bg-white" dangerouslySetInnerHTML={{ __html: signaturePreview }} />
                </div>
              )}

              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => loadSignaturePreview()}>
                  {language === 'es' ? 'Vista previa' : 'Preview'}
                </Button>
                <Button onClick={saveSignature} disabled={savingSignature} className="bg-blue-600 hover:bg-blue-700" data-testid="save-signature-btn">
                  {savingSignature ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  {language === 'es' ? 'Guardar Firma' : 'Save Signature'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Duplicate-send warning modal — fires BEFORE the 5s countdown */}
      <Dialog
        open={!!dupModal}
        onOpenChange={(open) => { if (!open) setDupModal(null); }}
      >
        <DialogContent className="max-w-lg" data-testid="duplicate-email-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              {language === 'es' ? 'Email ya enviado antes' : 'Email already sent before'}
            </DialogTitle>
            <DialogDescription>
              {language === 'es'
                ? 'Has enviado este mismo asunto a estos destinatarios anteriormente:'
                : 'You already sent this subject to these recipients before:'}
            </DialogDescription>
          </DialogHeader>
          {dupModal && (
            <div className="max-h-72 overflow-auto border rounded-md divide-y">
              {dupModal.duplicates.map((d) => {
                const dt = d.last_sent_at ? new Date(d.last_sent_at) : null;
                const dtStr = dt && !isNaN(dt.getTime())
                  ? dt.toLocaleString(language === 'es' ? 'es-ES' : 'en-US', {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit'
                    })
                  : '-';
                return (
                  <div key={d.email} className="p-3 text-sm">
                    <div className="font-medium">{d.email}</div>
                    <div className="text-muted-foreground text-xs mt-0.5">
                      {language === 'es' ? 'Último envío' : 'Last sent'}: <strong>{dtStr}</strong>
                      {' · '}
                      {language === 'es' ? 'Veces' : 'Times'}: <strong>{d.count}</strong>
                      {' · '}
                      {(d.sources || []).map(s =>
                        s === 'email_tool'
                          ? (language === 'es' ? 'Email Tool' : 'Email Tool')
                          : (language === 'es' ? 'SEO Prospector' : 'SEO Prospector')
                      ).join(', ')}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setDupModal(null)}
              data-testid="duplicate-cancel-btn"
            >
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              onClick={() => {
                const body = dupModal?.bodyToSend;
                setDupModal(null);
                if (body) _startSendCountdown(body);
              }}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="duplicate-confirm-resend-btn"
            >
              {language === 'es' ? 'Sí, reenviar' : 'Yes, resend'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
    </div>
  );
};

export default EmailToolTab;
